import 'clipsal-cortex-utils/src/build/polyfills';
import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import { store } from './app/store';
import { Provider } from 'react-redux';
import * as serviceWorker from './serviceWorker';
import { ENV_TYPE } from './env-type';
import { Amplify, API } from 'aws-amplify';
import 'regenerator-runtime/runtime';
import theme from './styles/theme';
import { Center, ChakraProvider, ColorModeScript, Heading } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import LogRocket from 'logrocket';
import { Capacitor } from '@capacitor/core';
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';
import 'focus-visible/dist/focus-visible'; // Removes blue border when clicking focusable items
import CombinedContextProvider from './context/CombinedContextProvider';
import packageJSON from '../package.json';
import { ENDPOINT, IS_PRODUCTION_BUILD, IS_RUNNING_CYPRESS_TESTS } from './common/constants';

const container = document.getElementById('root');
const root = createRoot(container!);

/**
 * Configures the AWS Amplify end-point setup for this repo.
 *
 * Note that we need to separately configure the `API` class, as for some reason in this monorepo architecture it does
 * not correctly initialize itself.
 *
 * see: https://github.com/aws-amplify/amplify-js/issues/5756
 */
function setupAPI() {
  const apiConfig = {
    endpoints: [
      {
        name: 'site',
        endpoint: ENDPOINT,
        region: 'ap-southeast-2',
      },
    ],
  };

  Amplify.configure({
    Auth: {
      region: 'ap-southeast-2',
      userPoolId: import.meta.env?.[`VITE_${ENV_TYPE}_USER_POOL_ID`],
      userPoolWebClientId: import.meta.env?.[`VITE_${ENV_TYPE}_CLIENT_ID`],
      authenticationFlowType: 'CUSTOM_AUTH',
    },
    API: apiConfig,
  });

  API.configure(apiConfig);
}

setupAPI();

async function setupReactDOM() {
  // This is only here to prompt for user tracking permissions on app launch, then the result is used elsewhere
  // (i.e. when a user is logged in). Just ensures the prompt occurs on app launch, rather than at login time.
  if (IS_PRODUCTION_BUILD && Capacitor.getPlatform() === 'ios') {
    await AppTrackingTransparency.requestPermission();
  }

  // Only send events to sentry in staging and production environments
  if (import.meta.env['MODE'] !== 'development' && !IS_RUNNING_CYPRESS_TESTS) setupSentry();

  root.render(
    <React.StrictMode>
      <Sentry.ErrorBoundary
        fallback={
          <Center h={'100vh'} w={'100%'}>
            <Heading textAlign={'center'}>
              An error occurred. Please refresh the page to continue. If this error persists, try closing and re-opening
              the app.
            </Heading>
          </Center>
        }
      >
        <Provider store={store}>
          <ChakraProvider theme={theme}>
            <CombinedContextProvider>
              <ColorModeScript />
              <App />
            </CombinedContextProvider>
          </ChakraProvider>
        </Provider>
      </Sentry.ErrorBoundary>
    </React.StrictMode>
  );
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();

/**
 * Configures the Sentry setup for this repo.
 */
function setupSentry() {
  Sentry.init({
    release: `fleet(${packageJSON.version})@` + import.meta.env.VITE_SENTRY_RELEASE,
    dsn: import.meta.env['VITE_SENTRY_DSN'] as string,
    beforeSend(event) {
      // To pass the session URL with a timestamp to the moment that an error is caught by Sentry,
      // we include the LogRocket URL in Sentry's beforeSend() action
      const logRocketSession = LogRocket.sessionURL;
      if (logRocketSession !== null) {
        if (!event.extra) event.extra = {};
        event.extra['LogRocket'] = logRocketSession;
        return event;
      } else {
        return event;
      }
    },
    // This sets the sample rate to be 5%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.05,
    // If the entire session is not sampled, use the below sample
    // rate to sample sessions when an error occurs
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      new Sentry.BrowserTracing({
        tracingOrigins: [ENDPOINT, 'https://fleet.clipsalcortex.com'],
        beforeNavigate: (context) => {
          return {
            ...context,
            name: window.location.pathname.replace(/\d+/g, ':id').replace(/[a-f0-9]{32}/g, '<hash>'),
          };
        },
      }),
      new Sentry.Replay(),
    ],
    environment: import.meta.env['MODE'],

    // Setting sample rate to 5% for finer control
    tracesSampleRate: 0.05,
  });
}

setupReactDOM();
